import Vue from 'vue'
import Vuex from 'vuex'
import router from '@/router'
import * as MUTATIONS from './mutation-types.js'
import defaultState from './state'
import {VuexPersistence} from "vuex-persist";
import {leadsModule} from "@/store/leads";
import {companyModule} from "@/store/company";
import {coverageModule} from "@/store/coverage";
import {businessUnitContactDetailsModule} from "@/store/businessUnitContactDetails";
import { captureError } from "@/services/userMonitoring";

Vue.use(Vuex);

let newState = {};
Object.assign(newState, defaultState);
const vuexLocal = new VuexPersistence({
  key: 'installer-dashboard'
});

export default new Vuex.Store({
  plugins: [vuexLocal.plugin],
  state: newState,
  mutations: {
    [MUTATIONS.USER_STATE]: (state, user_state) => {state.USER_STATE = user_state },
    [MUTATIONS.USER_ACCOUNT_LOCKED]: (state, user_account_locked) => {state.USER_ACCOUNT_LOCKED = user_account_locked },
    [MUTATIONS.NOTIFICATION_BUSY_STATUS]: (state, status) => {state.NOTIFICATION_BUSY_STATUS = status },
    [MUTATIONS.CLEAR]: (state) => { Object.assign(state, defaultState) },
    [MUTATIONS.ERRORS]: (state, value) => {state.ERRORS = {...value}},
    [MUTATIONS.NOTIFICATIONS]: (state, notificationSet) => {state.NOTIFICATIONS = [...notificationSet]},
    [MUTATIONS.BUSY]: (state, value) => {state.BUSY = value},
    [MUTATIONS.MAX_UPLOAD_SIZE]: (state, value) => {state.MAX_UPLOAD_SIZE = value},
    [MUTATIONS.MY_LEADS_RESULT_COUNT]: (state, value) => {state.MY_LEADS_RESULT_COUNT = value},
    [MUTATIONS.BUY_LEADS_RESULT_COUNT]: (state, value) => {state.BUY_LEADS_RESULT_COUNT = value},
    [MUTATIONS.HIDE_WELCOME_MESSAGE]: (state, value) => {state.HIDE_WELCOME_MESSAGE = value},
    [MUTATIONS.REMOVE_ERROR]: (state, key) => {
      if (state.ERRORS[key]) {
        delete state.ERRORS[key];
      }
      state.ERRORS = {...state.ERRORS};
    },
    [MUTATIONS.UPDATE_FILTER]: (state, {group, filters}) => {
      state.FILTERS = {...state.FILTERS, [group]:filters};
    },
    [MUTATIONS.CLEAR_FILTER]: (state, group) => {
      state.FILTERS = {...state.FILTERS, [group]: {}};
    },
    [MUTATIONS.CLEAR_PRE_SELECT]: (state, filterKey) => {
      if (state.FILTERS[filterKey].lead) {
        delete state.FILTERS[filterKey].lead;
      }
      if (state.FILTERS[filterKey].enquiry) {
        delete state.FILTERS[filterKey].enquiry;
      }
    },
    [MUTATIONS.IS_MOBILE]: (state, value) => {state.IS_MOBILE = value},
    [MUTATIONS.IS_TABLET]: (state, value) => {state.IS_TABLET = value},
    [MUTATIONS.TODOS]: (state, value) => {state.TODOS = {...value}},
    [MUTATIONS.ADD_TODO]: (state, value) => {state.TODOS = {...state.TODOS, [value.key]: value}},
    [MUTATIONS.REMOVE_TODO]: (state, key) => {
      if (state.TODOS[key]) {
        delete state.TODOS[key];
      }
      state.TODOS = {...state.TODOS};
    },
    [MUTATIONS.FIRST_BULK_BUY]: (state, value) => {state.FIRST_BULK_BUY = value},
  },
  getters: {
    newNotifications: state => state.NOTIFICATIONS.length,
    getFilter: (state) => (group) => (state.FILTERS[group]) ?? {}
  },
  actions: {
    logOut: async function({dispatch}) {
      return await this._vm.$api.auth.clear().finally(() => {
        dispatch('clearAll').then(() => {
          router.push({name: 'log-in'})
        });
      });
    },
    checkState: async function({commit}) {
      let currentState = false;
      try {
        let response = await this._vm.$api.auth.authorize();
        if(response.status === 202) {
          currentState = true;
        }
      } catch (e) {
        currentState = false;
        captureError(e, {
            section: "auth",
            action: "authorize",
            type: "api-request",
        });
      }

      commit(MUTATIONS.USER_STATE, currentState);
      return currentState;
    },
    authenticate: async function({commit}, credentials) {
      let state = false;
      let userAccountLocked = false;
      try {
        let response = await this._vm.$api.auth.authenticate(
          credentials.username,
          credentials.password
        );
        if(response.status === 202) {
          state = true;
        }
      } catch (e) {
        if (e.response.status !== 400 && e.response.status !== 403) {
          captureError(e, {
            section: "auth",
            action: "authenticate",
            type: "api-request",
          });
        }
        if (e.response.status === 403) {
          userAccountLocked = true;
        }
        state = false;
      }

      commit(MUTATIONS.USER_STATE, state);
      commit(MUTATIONS.USER_ACCOUNT_LOCKED, userAccountLocked);
    },
    tokenAuth: async function({commit}, credentials) {
      let state = false;
      try {
        let response = await this._vm.$api.auth.tokenAuthenticate(credentials);
        if(response.status === 202) {
          state = true;
        }
      } catch (e) {
        captureError(e, {
          section: "auth",
          action: "token-authenticate",
          type: "api-request",
        });
        state = false;
      }

      commit(MUTATIONS.USER_STATE, state);
    },
    updateGlobals: async function({dispatch}) {
      dispatch("accountBalance");
      dispatch("leadAlertStatus");
      dispatch("getTodos");
      dispatch("getContactDetails");
      dispatch('getCompany').then(() => {
        dispatch('getLeadNotifications');
        this._vm.$eb.$emit('globals-companyUpdated');
      });
    },
    getNotifications: async function ({commit}) {
      try{
        commit(MUTATIONS.NOTIFICATION_BUSY_STATUS, true);
        let response = await this._vm.$api.notification.getNew();
        if(response.status === 200) {
          commit(MUTATIONS.NOTIFICATION_BUSY_STATUS, false);
          commit(MUTATIONS.NOTIFICATIONS, response.data.data);
        }
      } catch (e) {
        commit(MUTATIONS.NOTIFICATION_BUSY_STATUS, false);
        captureError(e, {
            section: "polls",
            action: "get-notifications",
            type: "api-request",
        });
      }
    },
    getTodos: async function({commit}) {
      const response = await this._vm.$api.todos.list();
      commit(MUTATIONS.TODOS, response.data.data);
    },
    systemNotificationRead: async function({commit, state}, data) {
      this._vm.$api.notification.clearSystem(data.id)
        .then(() => {
          commit(MUTATIONS.NOTIFICATIONS, state.NOTIFICATIONS.filter(n => n.id !== data.id));
        })
        .catch(e => {
          captureError(e, {
              section: "polls",
              action: "get-system-notifications",
              type: "api-request",
          });
        });
    },
    notificationRead: async function({commit, state}, data) {
      this._vm.$api.notification.clear(data.id)
        .then(() => {
          commit(MUTATIONS.NOTIFICATIONS, state.NOTIFICATIONS.filter(n => n.id !== data.id));
        })
        .catch((e) => {
          captureError(e, {
            section: "polls",
            action: "clear-notification",
            type: "api-request",
          });
        })
    },
    clearNotifications: async function({dispatch}) {
      await dispatch('clearStoredNotifications');
      this._vm.$api.notification.clearAll().then(() => {
        dispatch('getNotifications');
      });
    },
    clearStoredNotifications: function({commit, state}) {
      let nonDismissable = state.NOTIFICATIONS.filter((notification) => !notification.dismissable);
      commit(MUTATIONS.NOTIFICATIONS, nonDismissable);
    },
    clearAll: async function({commit, state}) {
      if (state.leads.LEAD_NOTIFICATION_INTERVAL) {
        clearInterval(state.leads.LEAD_NOTIFICATION_INTERVAL);
      }
      localStorage.removeItem('installer-dashboard');
      commit(MUTATIONS.CLEAR);
    },
    getMaxUploadSize: async function({commit}) {
      this._vm.$api.app.getAllowedImageSize()
        .then(response => {
          commit(MUTATIONS.MAX_UPLOAD_SIZE, response.data.data.size)
        })
        .catch(e => {
          captureError(e, {
              section: "utility",
              action: "get-max-upload",
              type: "api-request",
          });
        })
    },
  },
  modules: {
    leads: leadsModule,
    company: companyModule,
    coverage: coverageModule,
    businessUnitContactDetails: businessUnitContactDetailsModule,
  }
})
